import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api/client";
import { announceLogout, refreshAccessToken } from "@/lib/auth/refresh";
import { setAccessToken, subscribeToAccessToken } from "@/lib/auth/token-store";

type AuthState = "loading" | "authenticated" | "anonymous";
type AuthContextValue = {
  state: AuthState;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
};
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>("loading");
  useEffect(() => {
    void refreshAccessToken().then((token) =>
      setState(token ? "authenticated" : "anonymous"),
    );
    return subscribeToAccessToken((token) =>
      setState(token ? "authenticated" : "anonymous"),
    );
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await api.POST("/auth/login", {
      body: { email, password },
    });
    if (error || !data) return false;
    setAccessToken(data.access_token);
    setState("authenticated");
    return true;
  }, []);

  const logout = useCallback(async () => {
    await api.POST("/auth/logout");
    announceLogout();
    setState("anonymous");
  }, []);

  const value = useMemo(
    () => ({ state, login, logout }),
    [state, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
